import { defineStore } from 'pinia';
import { TokenResponse, User } from 'src/api';
import { RemovableRef, useLocalStorage } from '@vueuse/core';
import { UserPermissions } from 'src/service/UserPermissions';
import { Ref, ref } from 'vue';

function getRefreshToken(session: TokenResponse | null): string | null {
  if (
    session &&
    session.refreshToken &&
    (session.refreshTokenExpires ?? 0) > Date.now() / 1000
  ) {
    return session.refreshToken;
  }
  return null;
}

function getAccessToken(session: TokenResponse | null): string | null {
  if (
    session &&
    session.accessToken &&
    (session.accessTokenExpires ?? 0) > Date.now() / 1000
  ) {
    return session.accessToken;
  }
  return null;
}

function renderUserName(user?: User): string {
  return [user?.firstName, user?.lastName].filter((value) => !!value).join(' ');
}

// keeps the auth and refresh tokens with info about currently logged in user.
// user is logged in while there is a valid refresh token
// because if the accessToken expires, an interceptor automatically renews it if the refresh token is valid.
// if the renew fails, we auto log out. if the refreshToken is invalid, auto log out
export const useSession = defineStore('session', {
  state: () => {
    const tokenRef = useLocalStorage(
      `session${process.env.BUILD ?? ''}`,
      {},
    ) as RemovableRef<TokenResponse>;
    const impersonating: Ref<User | undefined> = ref();
    return {
      current: tokenRef,
      impersonating,
      permission: new UserPermissions(tokenRef.value.user ?? {}),
      preLoginUrl: '',
    };
  },
  getters: {
    user: (state) => state.impersonating ?? state.current?.user,
    userId(): number {
      return this.user?.id ?? 0;
    },
    userEmail(): string {
      return this.user?.email ?? '';
    },
    userPhone(): string {
      return this.user?.phone ?? '';
    },
    userFirstName(): string {
      return this.user?.firstName ?? '';
    },
    userLastName(): string {
      return this.user?.lastName ?? '';
    },
    userName(): string {
      return renderUserName(this.user);
    },
    accessToken: (state) =>
      getAccessToken(state.current) ? state.current?.accessToken : null,
    refreshToken: (state) =>
      getRefreshToken(state.current) ? state.current?.refreshToken : null,
    accessTokenIsValid: (state) => !!getAccessToken(state.current),
    refreshTokenIsValid: (state) => !!getRefreshToken(state.current),
    isLoggedIn: (state) => !!getRefreshToken(state.current),
    isImpersonating: (state) => !!state.impersonating,
  },
  actions: {
    login(token: TokenResponse) {
      this.current = token;
      this.permission = new UserPermissions(token.user ?? {});
      this.impersonating = undefined;
    },
    logout() {
      this.current = {};
      this.permission = new UserPermissions({});
      this.impersonating = undefined;
    },
    updateUser(user: User) {
      // user edited their own account: update it on save
      const existing = this.current.user;
      if (existing) {
        existing.firstName = user.firstName;
        existing.lastName = user.lastName;
      }
    },
    impersonate(user?: User) {
      if (!this.current.user) {
        // can only impersonate when already logged in
        return;
      }
      if (!!user && !this.permission.isSuperDude()) {
        // only allow admins to start impersonating
        return;
      }
      this.impersonating = user;
      this.permission = new UserPermissions(user ?? this.current.user ?? {});
    },
    getPreLoginUrl(): string {
      const url = this.preLoginUrl;
      this.preLoginUrl = '';
      return url;
    },
  },
});
